/**
 * Google OAuth Authentication Context
 * Manages Google Sign-In state for the SSD Intranet
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  type ReactNode,
} from 'react';

import type {
  AuthContextValue,
  AuthState,
  GoogleUserInfo,
  CredentialResponse,
} from '../types/auth';
import { resolveUserRoleProfile } from '../utils/roleUtils';
import { onAdminSettingsChange } from '../utils/adminSync';

// Robust Google OAuth Client ID resolution with production fallback
const DEFAULT_GOOGLE_CLIENT_ID =
  '74903365356-pcr5v18f182n027l647acoqph3k5o7aa.apps.googleusercontent.com';

function getValidClientId(): string {
  const envVal = import.meta.env.VITE_GOOGLE_CLIENT_ID;
  if (
    typeof envVal === 'string' &&
    envVal.trim().length > 10 &&
    envVal.trim() !== 'undefined' &&
    envVal.trim() !== 'null'
  ) {
    return envVal.trim();
  }
  return DEFAULT_GOOGLE_CLIENT_ID;
}

const GOOGLE_CLIENT_ID = getValidClientId();

// Session duration: 90 days (persistent session for workplace convenience)
const SESSION_DURATION_MS = 90 * 24 * 60 * 60 * 1000;

// Storage key for persisting auth state
const AUTH_STORAGE_KEY = 'ssd_intranet_auth';

// Token refresh buffer - refresh 5 minutes before Google token expiry
const TOKEN_REFRESH_BUFFER_MS = 5 * 60 * 1000;

// Initial auth state
const initialAuthState: AuthState = {
  isAuthenticated: false,
  isLoading: true,
  user: null,
  idToken: null,
  accessToken: null,
  error: null,
};

// Create context with default value
const AuthContext = createContext<AuthContextValue | null>(null);

// Decode JWT token payload (without verification - server will verify)
function decodeJwt(token: string): GoogleUserInfo | null {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload) as GoogleUserInfo;
  } catch {
    return null;
  }
}

// Check if user session has expired (90-day session duration)
function isSessionExpired(sessionExpiresAt?: number, exp?: number): boolean {
  if (sessionExpiresAt && typeof sessionExpiresAt === 'number') {
    return Date.now() >= sessionExpiresAt;
  }
  if (exp && typeof exp === 'number') {
    const issuedAtMs = (exp - 3600) * 1000;
    return Date.now() >= issuedAtMs + SESSION_DURATION_MS;
  }
  return true;
}

// Synchronously restore authentication state from localStorage at 0ms
function getStoredAuthState(): AuthState {
  try {
    const stored = localStorage.getItem(AUTH_STORAGE_KEY);
    if (stored) {
      const { idToken, exp, sessionExpiresAt } = JSON.parse(stored);
      if (!isSessionExpired(sessionExpiresAt, exp)) {
        const userInfo = decodeJwt(idToken);
        if (userInfo) {
          const profile = resolveUserRoleProfile(userInfo.email);
          const enrichedUser = {
            ...userInfo,
            roles: profile.roles,
            primaryTeam: profile.primaryTeam,
            assignedTeams: profile.assignedTeams || [],
          };

          return {
            isAuthenticated: true,
            isLoading: false,
            user: enrichedUser,
            idToken,
            accessToken: null,
            error: null,
          };
        }
      }
    }
  } catch {
    // Storage error, fallback to initialAuthState
  }
  return initialAuthState;
}

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [authState, setAuthState] = useState<AuthState>(() => getStoredAuthState());
  const googleInitialized = useRef(false);

  // Handle successful Google Sign-In
  const handleCredentialResponse = useCallback((response: CredentialResponse) => {
    const userInfo = decodeJwt(response.credential);

    if (userInfo) {
      const profile = resolveUserRoleProfile(userInfo.email);
      const enrichedUser = {
        ...userInfo,
        roles: profile.roles,
        primaryTeam: profile.primaryTeam,
        assignedTeams: profile.assignedTeams || [],
      };

      const newState: AuthState = {
        isAuthenticated: true,
        isLoading: false,
        user: enrichedUser,
        idToken: response.credential,
        accessToken: null,
        error: null,
      };

      setAuthState(newState);

      // Persist to localStorage with 90-day session expiration
      try {
        const sessionExpiresAt = Date.now() + SESSION_DURATION_MS;
        localStorage.setItem(
          AUTH_STORAGE_KEY,
          JSON.stringify({
            idToken: response.credential,
            exp: userInfo.exp,
            sessionExpiresAt,
          })
        );
      } catch {
        // Storage might be unavailable
      }
    } else {
      setAuthState({
        ...initialAuthState,
        isLoading: false,
        error: 'Invalid token format',
      });
    }
  }, []);

  // Initialize Google Identity Services
  useEffect(() => {
    if (googleInitialized.current || !GOOGLE_CLIENT_ID) {
      if (!GOOGLE_CLIENT_ID) {
        setAuthState({
          ...initialAuthState,
          isLoading: false,
          error: 'Google Client ID not configured',
        });
      }
      return;
    }

    // Check for existing session
    let isRestored = authState.isAuthenticated;
    if (!isRestored) {
      try {
        const stored = localStorage.getItem(AUTH_STORAGE_KEY);
        if (stored) {
          const { idToken, exp, sessionExpiresAt } = JSON.parse(stored);
          if (!isSessionExpired(sessionExpiresAt, exp)) {
            const userInfo = decodeJwt(idToken);
            if (userInfo) {
              const profile = resolveUserRoleProfile(userInfo.email);
              const enrichedUser = {
                ...userInfo,
                roles: profile.roles,
                primaryTeam: profile.primaryTeam,
                assignedTeams: profile.assignedTeams || [],
              };

              setAuthState({
                isAuthenticated: true,
                isLoading: false,
                user: enrichedUser,
                idToken,
                accessToken: null,
                error: null,
              });
              isRestored = true;
            }
          }
          if (!isRestored) {
            console.log('[Auth] Session expired after 90 days, clearing storage');
            localStorage.removeItem(AUTH_STORAGE_KEY);
          }
        }
      } catch {
        // Storage error, continue with fresh init
      }
    }

    // Wait for Google Identity Services to load (with timeout)
    let retryCount = 0;
    const maxRetries = 50; // 5 seconds max (50 * 100ms)

    const initGoogle = () => {
      retryCount++;

      if (!window.google?.accounts?.id) {
        if (retryCount >= maxRetries) {
          // GSI failed to load - might be blocked by ad blocker
          console.warn('[Auth] Google Identity Services failed to load after 5s. May be blocked by browser extension.');
          if (!isRestored) {
            setAuthState({
              ...initialAuthState,
              isLoading: false,
              error: 'Google Sign-In unavailable. Check if ad blocker is enabled.',
            });
          }
          return;
        }
        // GSI not loaded yet, retry
        setTimeout(initGoogle, 100);
        return;
      }

      console.log('[Auth] Google Identity Services loaded, initializing...');

      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: handleCredentialResponse,
        auto_select: !isRestored, // Auto-sign-in if no valid session
        cancel_on_tap_outside: true,
      });

      googleInitialized.current = true;
      if (!isRestored) {
        setAuthState((prev) => ({ ...prev, isLoading: false }));
      }
      console.log('[Auth] Initialization complete');

      // If not authenticated, trigger One Tap/Auto-select
      if (!isRestored) {
        console.log('[Auth] No restored session, attempting auto-select/One Tap');
        window.google.accounts.id.prompt((notification) => {
          if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
            console.log('[Auth] Auto-select/One Tap not displayed:', notification.getNotDisplayedReason() || notification.getSkippedReason());
          } else if (notification.isDismissedMoment()) {
            console.log('[Auth] Auto-select dismissed');
          } else {
            console.log('[Auth] Auto-select prompt displayed');
          }
        });
      }
    };

    console.log('[Auth] Starting initialization, Client ID:', GOOGLE_CLIENT_ID ? 'present' : 'MISSING');
    initGoogle();
  }, [handleCredentialResponse]);

  // Subscribe to admin settings changes to dynamically refresh user's assigned roles/teams
  useEffect(() => {
    const unsubscribe = onAdminSettingsChange(() => {
      setAuthState((prev) => {
        if (!prev.isAuthenticated || !prev.user) return prev;
        
        // Re-resolve the user's role profile with the newly fetched global admin settings
        const newProfile = resolveUserRoleProfile(prev.user.email);
        
        // If roles or teams changed, update the auth state
        if (
          JSON.stringify(prev.user.roles) !== JSON.stringify(newProfile.roles) ||
          prev.user.primaryTeam !== newProfile.primaryTeam ||
          JSON.stringify(prev.user.assignedTeams) !== JSON.stringify(newProfile.assignedTeams)
        ) {
          console.log('[Auth] Admin settings changed. Re-resolved user profile:', newProfile);
          return {
            ...prev,
            user: {
              ...prev.user,
              roles: newProfile.roles,
              primaryTeam: newProfile.primaryTeam,
              assignedTeams: newProfile.assignedTeams || [],
            }
          };
        }
        
        return prev;
      });
    });

    return () => unsubscribe();
  }, []);

  // Sign in - trigger Google One Tap or redirect
  const signIn = useCallback(() => {
    if (!window.google?.accounts?.id) {
      setAuthState((prev) => ({
        ...prev,
        error: 'Google Sign-In not available',
      }));
      return;
    }

    // Use One Tap prompt
    window.google.accounts.id.prompt((notification) => {
      if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
        // One Tap not available, could show a button instead
        const reason = notification.getNotDisplayedReason() || notification.getSkippedReason();
        console.log('[Auth] One Tap not shown:', reason);
      }
    });
  }, []);

  // Silent token refresh using Google One Tap
  const refreshToken = useCallback(() => {
    if (!window.google?.accounts?.id || !authState.isAuthenticated) {
      return;
    }

    console.log('[Auth] Attempting silent token refresh...');

    // Trigger One Tap with auto_select to silently refresh
    window.google.accounts.id.prompt((notification) => {
      const momentType = notification.getMomentType();
      if (momentType === 'display') {
        console.log('[Auth] Token refresh prompt displayed');
      } else if (notification.isSkippedMoment()) {
        console.log('[Auth] Token refresh skipped:', notification.getSkippedReason());
      } else if (notification.isNotDisplayed()) {
        console.log('[Auth] Token refresh not displayed:', notification.getNotDisplayedReason());
      } else if (notification.isDismissedMoment()) {
        console.log('[Auth] Token refresh dismissed:', notification.getDismissedReason());
      }
    });
  }, [authState.isAuthenticated]);

  // Set up automatic token refresh before expiry
  useEffect(() => {
    if (!authState.isAuthenticated || !authState.user?.exp) {
      return;
    }

    const expiresAt = authState.user.exp * 1000;
    const now = Date.now();
    const timeUntilRefresh = expiresAt - now - TOKEN_REFRESH_BUFFER_MS;

    if (timeUntilRefresh <= 0) {
      // Token is already close to expiring or expired, refresh now
      refreshToken();
      return;
    }

    console.log(`[Auth] Token refresh scheduled in ${Math.round(timeUntilRefresh / 60000)} minutes`);

    const refreshTimer = setTimeout(() => {
      console.log('[Auth] Token refresh timer triggered');
      refreshToken();
    }, timeUntilRefresh);

    return () => clearTimeout(refreshTimer);
  }, [authState.isAuthenticated, authState.user?.exp, refreshToken]);

  // Sign out
  const signOut = useCallback(() => {
    // Revoke token with Google
    if (window.google?.accounts?.id && authState.user?.email) {
      window.google.accounts.id.revoke(authState.user.email, () => {
        console.log('[Auth] Token revoked');
      });
    }

    // Clear state
    setAuthState({
      ...initialAuthState,
      isLoading: false,
    });

    // Clear storage
    try {
      localStorage.removeItem(AUTH_STORAGE_KEY);
    } catch {
      // Storage might be unavailable
    }

    // Disable auto-select for future page loads
    if (window.google?.accounts?.id) {
      window.google.accounts.id.disableAutoSelect();
    }
  }, [authState.user?.email]);

  // Listen for global auth-expired events (e.g. from API client)
  useEffect(() => {
    const handleAuthExpired = () => {
      console.log('[Auth] Global auth-expired event received, forcing sign out');
      signOut();
    };

    window.addEventListener('auth-expired', handleAuthExpired);
    return () => window.removeEventListener('auth-expired', handleAuthExpired);
  }, [signOut]);


  // Preview Mode State (Allowed ONLY for agonzalez@ssdspc.com and mm@ssdspc.com)
  const realUser = authState.user;
  const realUserEmail = (realUser?.email || '').toLowerCase().trim();
  const canPreview = realUserEmail === 'agonzalez@ssdspc.com' || realUserEmail === 'mm@ssdspc.com';

  const [previewUserEmailState, setPreviewUserEmailState] = useState<string | null>(() => {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const queryPreview = urlParams.get('previewUser');
      if (queryPreview) {
        const cleaned = queryPreview.toLowerCase().trim();
        localStorage.setItem('ssd_preview_user_email', cleaned);
        return cleaned;
      }
      return localStorage.getItem('ssd_preview_user_email');
    } catch {
      return null;
    }
  });

  const setPreviewUserEmail = useCallback((email: string | null) => {
    setPreviewUserEmailState(email);
    try {
      if (email) {
        localStorage.setItem('ssd_preview_user_email', email);
      } else {
        localStorage.removeItem('ssd_preview_user_email');
      }
    } catch {
      // Ignore storage error
    }
  }, []);

  const effectiveUser = useMemo(() => {
    if (canPreview && previewUserEmailState && realUser) {
      const profile = resolveUserRoleProfile(previewUserEmailState);
      return {
        ...realUser,
        sub: `preview_${previewUserEmailState}`,
        email: previewUserEmailState,
        name: `Preview User (${previewUserEmailState})`,
        given_name: previewUserEmailState.split('@')[0],
        roles: profile.roles,
        primaryTeam: profile.primaryTeam,
        assignedTeams: profile.assignedTeams || [],
      };
    }
    return realUser;
  }, [realUser, canPreview, previewUserEmailState]);

  const value: AuthContextValue = {
    ...authState,
    user: effectiveUser,
    realUser,
    canPreview,
    previewUserEmail: canPreview ? previewUserEmailState : null,
    setPreviewUserEmail,
    signIn,
    signOut,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

// Hook to use auth context
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// Hook to get the Google button renderer
export function useGoogleButton() {
  const renderButton = useCallback((element: HTMLElement | null) => {
    console.log('[Auth] renderButton called, element:', !!element, 'google:', !!window.google?.accounts?.id, 'clientId:', !!GOOGLE_CLIENT_ID);

    if (!element) {
      console.warn('[Auth] renderButton: No element provided');
      return;
    }
    if (!window.google?.accounts?.id) {
      console.warn('[Auth] renderButton: Google Identity Services not available');
      return;
    }
    if (!GOOGLE_CLIENT_ID) {
      console.warn('[Auth] renderButton: No Client ID');
      return;
    }

    try {
      window.google.accounts.id.renderButton(element, {
        type: 'standard',
        theme: 'outline',
        size: 'large',
        text: 'signin_with',
        shape: 'rectangular',
        logo_alignment: 'left',
      });
      console.log('[Auth] renderButton: Button rendered successfully');
    } catch (err) {
      console.error('[Auth] renderButton error:', err);
    }
  }, []);

  return { renderButton };
}

// Export token getter for API client
export function getAuthToken(): string | null {
  try {
    const stored = localStorage.getItem(AUTH_STORAGE_KEY);
    if (stored) {
      const { idToken, exp, sessionExpiresAt } = JSON.parse(stored);
      if (!isSessionExpired(sessionExpiresAt, exp)) {
        return idToken;
      }
    }
  } catch {
    // Storage error
  }
  return null;
}

// Export function to check if token needs refresh soon
export function shouldRefreshToken(): boolean {
  try {
    const stored = localStorage.getItem(AUTH_STORAGE_KEY);
    if (stored) {
      const { exp } = JSON.parse(stored);
      const expiresAt = (exp || 0) * 1000;
      const now = Date.now();
      // Return true if token expires within the buffer period
      return expiresAt - now < TOKEN_REFRESH_BUFFER_MS;
    }
  } catch {
    // Storage error
  }
  return false;
}
